import { Platform } from 'react-native';
import Toast from 'react-native-toast-message';

/**
 * Converts an HTML string to a PDF and triggers a download (web) or share dialog (native).
 *
 * @param {Object} options
 * @param {string} options.html - The HTML content string.
 * @param {string} options.filename - The output filename (without extension).
 * @param {string} [options.title] - The title shown in the native share dialog.
 * @param {string} [options.orientation] - 'portrait' | 'landscape' (default 'landscape').
 * @param {number} [options.windowWidth] - The CSS viewport width used when rendering the HTML
 *                                         before scaling into the PDF. Wider values produce
 *                                         sharper text but may require more memory.
 */
export const exportHTMLToPDF = async ({
    html,
    filename,
    title = 'PDF Export',
    orientation = 'landscape',
    windowWidth = 1400,
    pageBreakSelector = '.page-break-before',
}) => {
    const safeFilename = filename.replace(/[^a-zA-Z0-9_-]/g, '_');

    if (Platform.OS === 'web') {
        const { jsPDF } = await import('jspdf');

        const doc = new jsPDF({
            orientation,
            unit: 'mm',
            format: 'letter',
        });

        // Convert external image URLs to base64 data URLs so html2canvas
        // can render them without CORS restrictions.
        const toDataURL = async (url) => {
            try {
                const response = await fetch(url);
                const blob = await response.blob();
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            } catch (e) {
                console.warn('Failed to fetch image for PDF:', url, e);
                return url;
            }
        };

        let processedHtml = html;
        const imgRegex = /<img[^>]+src=["']([^"']+)["']/gi;
        const imgUrls = [];
        let match;
        while ((match = imgRegex.exec(html)) !== null) {
            const url = match[1];
            if (url.startsWith('http') && !url.startsWith('data:')) {
                imgUrls.push(url);
            }
        }

        const uniqueUrls = [...new Set(imgUrls)];
        const base64Map = {};
        await Promise.all(
            uniqueUrls.map(async (url) => {
                base64Map[url] = await toDataURL(url);
            })
        );

        processedHtml = processedHtml.replace(imgRegex, (fullMatch, url) => {
            if (base64Map[url]) {
                return fullMatch.replace(url, base64Map[url]);
            }
            return fullMatch;
        });

        // Render HTML in an off-screen iframe via a blob URL so the browser
        // parses it as a real document (head styles, meta tags, etc.).
        const blob = new Blob([processedHtml], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);

        const iframe = document.createElement('iframe');
        iframe.style.cssText =
            'position:absolute;left:-9999px;top:0;width:' +
            windowWidth +
            'px;height:100%;border:0;overflow:auto;';
        iframe.src = blobUrl;
        document.body.appendChild(iframe);

        await new Promise((resolve) => {
            iframe.onload = resolve;
        });

        // Force light-mode background on the iframe body in case dark theme leaks in
        const iframeBody = iframe.contentDocument?.body;
        if (iframeBody) {
            iframeBody.style.backgroundColor = '#ffffff';
            iframeBody.style.color = '#1f2937';
        }

        // Wait for fonts / layout to settle
        await new Promise((resolve) => setTimeout(resolve, 800));

        const html2canvas = (await import('html2canvas')).default;
        const pdfWidth = doc.internal.pageSize.getWidth() - 20; // 10mm margins
        const pageHeight = doc.internal.pageSize.getHeight() - 20;

        // Helper: render a set of visible body children to canvas and add to PDF
        const renderToPDF = async (visibleChildren) => {
            const iframeDoc = iframe.contentDocument;
            // Hide all body children, then show only the ones for this segment
            const allChildren = Array.from(iframeBody.children);
            allChildren.forEach(child => { child.style.display = 'none'; });
            visibleChildren.forEach(child => { child.style.display = ''; });

            // Shrink body/html to fit only visible content (iframe has height:100%
            // which keeps body tall even when children are hidden → blank pages)
            const prevBodyHeight = iframeDoc.body.style.height;
            const prevHtmlHeight = iframeDoc.documentElement.style.height;
            iframeDoc.body.style.height = 'auto';
            iframeDoc.documentElement.style.height = 'auto';
            // Force reflow so layout settles before measuring
            void iframeDoc.body.offsetHeight;

            // Measure actual content height from visible children
            const bodyRect = iframeBody.getBoundingClientRect();
            let contentBottom = 0;
            visibleChildren.forEach(child => {
                const rect = child.getBoundingClientRect();
                contentBottom = Math.max(contentBottom, rect.bottom - bodyRect.top);
            });

            const canvas = await html2canvas(iframeBody, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                width: windowWidth,
                height: contentBottom,
                windowWidth: windowWidth,
                logging: false,
            });

            // Restore heights
            iframeDoc.body.style.height = prevBodyHeight;
            iframeDoc.documentElement.style.height = prevHtmlHeight;

            const imgData = canvas.toDataURL('image/png');
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

            // Slice into pages at fixed height intervals
            let remainingHeight = pdfHeight;
            let yPosition = 10;

            doc.addImage(imgData, 'PNG', 10, yPosition, pdfWidth, pdfHeight);
            remainingHeight -= pageHeight;

            while (remainingHeight > 10) {
                doc.addPage();
                yPosition = -(pdfHeight - remainingHeight - 10);
                doc.addImage(imgData, 'PNG', 10, yPosition, pdfWidth, pdfHeight);
                remainingHeight -= pageHeight;
            }
        };

        // Split body children into segments at page-break elements.
        // Each segment gets its own canvas → own page(s).
        const allChildren = Array.from(iframeBody.children);
        const hasBreaks = pageBreakSelector && allChildren.some(child =>
            child.matches?.(pageBreakSelector)
        );

        if (hasBreaks) {
            // Build segments: children before first break, then each break starts a new segment
            const segments = [];
            let currentSegment = [];
            for (const child of allChildren) {
                if (child.matches?.(pageBreakSelector)) {
                    if (currentSegment.length > 0) segments.push(currentSegment);
                    currentSegment = [child];
                } else {
                    currentSegment.push(child);
                }
            }
            if (currentSegment.length > 0) segments.push(currentSegment);

            for (let s = 0; s < segments.length; s++) {
                if (s > 0) doc.addPage();
                await renderToPDF(segments[s]);
            }

            // Restore all children visibility
            allChildren.forEach(child => { child.style.display = ''; });
        } else {
            // No break elements — render everything as one canvas
            await renderToPDF(allChildren);
        }

        doc.save(`${safeFilename}.pdf`);

        URL.revokeObjectURL(blobUrl);
        if (iframe.parentNode) {
            document.body.removeChild(iframe);
        }
    } else {
        const RNHTMLtoPDF = await import('react-native-html-to-pdf');
        const Sharing = await import('expo-sharing');

        const file = await RNHTMLtoPDF.generatePDF({
            html,
            fileName: safeFilename,
            directory: 'Documents',
            base64: false,
            width: 792,
            height: 612,
        });

        if (file?.filePath && (await Sharing.isAvailableAsync())) {
            await Sharing.shareAsync(file.filePath, {
                mimeType: 'application/pdf',
                dialogTitle: title,
            });
        }
    }
};

/**
 * Wrapper around exportHTMLToPDF that enables/disables a loader while the PDF is generated.
 */
export const exportHTMLToPDFWithLoader = async (options, loader) => {
    try {
        loader?.enable?.();
        await exportHTMLToPDF(options);
    } catch (error) {
        console.error('PDF export error:', error);
        Toast.show({
            type: 'error',
            text2: 'Failed to export PDF: ' + (error?.message || 'Unknown error'),
        });
    } finally {
        loader?.disable?.();
    }
};
